import { Injectable } from '@angular/core';
import * as Request from '@trusted-solutions/sdit.models/dist/lambda/request';
import * as Response from '@trusted-solutions/sdit.models/dist/lambda/response';
import { LambdaClientService } from './lambda-client.service';
import { Method } from './models';
import { FunctionNameEnum } from './function-name.enum';

@Injectable({
  providedIn: 'root'
})
export class TsSditDataService {

  constructor(
    private client: LambdaClientService) { }

  public readonly companyAdmin = {
    createStoreAdmin: (this.client.invoke.bind(
      this, FunctionNameEnum.createStoreAdminUser)) as Method<
        Request.CreateStoreAdminUserRequest, Response.CreateStoreAdminUserResponse>,
    enableUser: (this.client.invoke.bind(
      this, FunctionNameEnum.adminEnableUser)) as Method<
        Request.AdminEnableUserRequest, Response.AdminEnableUserResponse>,
    disableUser: (this.client.invoke.bind(
      this, FunctionNameEnum.adminDisableUser)) as Method<
        Request.AdminDisableUserRequest, Response.AdminDisableUserResponse>,
    setUserPassword: (this.client.invoke.bind(
      this, FunctionNameEnum.adminSetUserPassword)) as Method<
        Request.AdminSetUserPasswordRequest, Response.AdminSetUserPasswordResponse>,
    listStores: (this.client.invoke.bind(
      this, FunctionNameEnum.listStores)) as Method<
        Request.ListStoresRequest, Response.ListStoresResponse>,
    listUserInGroup: (this.client.invoke.bind(
      this, FunctionNameEnum.listUsersInGroup)) as Method<
        Request.ListUsersInGroupRequest, Response.ListUsersInGroupResponse>,
  };

  public readonly store = {
    create: (this.client.invoke.bind(
      this, FunctionNameEnum.createStore)) as Method<
        Request.CreateStoreRequest, Response.CreateStoreResponse>,
    update: (this.client.invoke.bind(
      this, FunctionNameEnum.updateStore)) as Method<
        Request.UpdateStoreRequest, Response.UpdateStoreResponse>,
    list: (this.client.invoke.bind(
      this, FunctionNameEnum.getStores)) as Method<
        Request.GetStoresRequest, Response.GetStoresResponse>,
    verifyAdminPinCode: (this.client.invoke.bind(
      this, FunctionNameEnum.verifyAdminPinCode)) as Method<
        Request.VerifyAdminPinCodeRequest, Response.VerifyAdminPinCodeResponse>,
    setAdminPinCode: (this.client.invoke.bind(
      this, FunctionNameEnum.setAdminPinCode)) as Method<
        Request.SetAdminPinCodeRequest, Response.SetAdminPinCodeResponse>,
  };

  public readonly kiosk = {
    create: (this.client.invoke.bind(
      this, FunctionNameEnum.createKiosk)) as Method<
        Request.CreateKioskRequest, Response.CreateKioskResponse>,
    list: (this.client.invoke.bind(
      this, FunctionNameEnum.getKiosks)) as Method<
        Request.GetKiosksRequest, Response.GetKiosksResponse>,
  };

  public readonly config = {
    getTimeInfo: (this.client.invoke.bind(
      this, FunctionNameEnum.getTimeInfo)) as Method<
        Request.GetTimeInfoRequest, Response.GetTimeInfoResponse>,
    synchronizeBlockStorage: (this.client.invoke.bind(
      this, FunctionNameEnum.synchronizeBlockStorage)) as Method<
        Request.SynchronizeBlockRequest, Response.SynchronizeBlockResponse>,
  };

  public readonly productCategory = {
    create: (this.client.invoke.bind(
      this, FunctionNameEnum.createProductCategory)) as Method<
        Request.CreateProductCategoryRequest, Response.CreateProductCategoryResponse>,
    update: (this.client.invoke.bind(
      this, FunctionNameEnum.updateProductCategory)) as Method<
        Request.UpdateProductCategoryRequest, Response.UpdateProductCategoryResponse>,
    reorder: (this.client.invoke.bind(
      this, FunctionNameEnum.reorderProductCategories)) as Method<
        Request.ReorderProductCategoriesRequest, Response.ReorderProductCategoriesResponse>,
    delete: (this.client.invoke.bind(
      this, FunctionNameEnum.deleteProductCategory)) as Method<
        Request.DeleteProductCategoryRequest, Response.DeleteProductCategoryResponse>,
    list: (this.client.invoke.bind(
      this.client, FunctionNameEnum.getProductCategories)) as Method<
        Request.GetProductCategoriesRequest, Response.GetProductCategoriesResponse>
  };

  public readonly product = {
    create: (this.client.invoke.bind(
      this, FunctionNameEnum.createProduct)) as Method<
        Request.CreateProductRequest, Response.CreateProductResponse>,
    update: (this.client.invoke.bind(
      this, FunctionNameEnum.updateProduct)) as Method<
        Request.UpdateProductRequest, Response.UpdateProductResponse>,
    reorder: (this.client.invoke.bind(
      this, FunctionNameEnum.reorderProducts)) as Method<
        Request.ReorderProductsRequest, Response.ReorderProductsResponse>,
    delete: (this.client.invoke.bind(
      this, FunctionNameEnum.deleteProduct)) as Method<
        Request.DeleteProductRequest, Response.DeleteProductResponse>,
    list: (this.client.invoke.bind(
      this.client, FunctionNameEnum.getProducts)) as Method<
        Request.GetProductsRequest, Response.GetProductsResponse>,
    soldOutSwitch: (this.client.invoke.bind(
      this.client, FunctionNameEnum.soldOutSwitch)) as Method<
        Request.SoldOutSwitchRequest, Response.SoldOutSwitchResponse>,
  };

  public readonly productAttributeCategory = {
    create: (this.client.invoke.bind(
      this, FunctionNameEnum.createProductAttributeCategory)) as Method<
        Request.CreateProductAttributeCategoryRequest, Response.CreateProductAttributeCategoryResponse>,
    update: (this.client.invoke.bind(
      this, FunctionNameEnum.updateProductAttributeCategory)) as Method<
        Request.UpdateProductAttributeCategoryRequest, Response.UpdateProductAttributeCategoryResponse>,
    reorder: (this.client.invoke.bind(
      this, FunctionNameEnum.reorderProductAttributeCategories)) as Method<
        Request.ReorderProductAttributeCategoriesRequest, Response.ReorderProductAttributeCategoriesResponse>,
    delete: (this.client.invoke.bind(
      this, FunctionNameEnum.deleteProductAttributeCategory)) as Method<
        Request.DeleteProductAttributeCategoryRequest, Response.DeleteProductAttributeCategoryResponse>,
  };

  public readonly productAttribute = {
    updateInCategory: (this.client.invoke.bind(
      this, FunctionNameEnum.updateProductAttributesInCategory)) as Method<
        Request.UpdateProductAttributesInCategoryRequest, Response.UpdateProductAttributesInCategoryResponse>,
    list: (this.client.invoke.bind(
      this, FunctionNameEnum.getProductAttributes)) as Method<
        Request.GetProductAttributesRequest, Response.GetProductAttributesResponse>,
  };

  public readonly invoice = {
    applyInvoiceNumber: (this.client.invoke.bind(
      this, FunctionNameEnum.applyInvoiceNumber)) as Method<
        Request.ApplyInvoiceNumberRequest, Response.ApplyInvoiceNumberResponse>,
    takeInvoiceNumber: (this.client.invoke.bind(
      this, FunctionNameEnum.takeInvoiceNumber)) as Method<
        Request.TakeInvoiceNumberRequest, Response.TakeInvoiceNumberResponse>,
    checkInvoiceNumber: (this.client.invoke.bind(
      this, FunctionNameEnum.checkInvoiceNumber)) as Method<
        Request.CheckInvoiceNumberRequest, Response.CheckInvoiceNumberResponse>,
    queryOfficeInfo: (this.client.invoke.bind(
      this, FunctionNameEnum.queryOfficeInfo)) as Method<
        Request.QueryOfficeInfoRequest, Response.QueryOfficeInfoResponse>,
    queryLoveCode: (this.client.invoke.bind(
      this, FunctionNameEnum.queryLoveCode)) as Method<
        Request.QueryLoveCodeRequest, Response.QueryLoveCodeResponse>,
  };

  public readonly order = {
    create: (this.client.invoke.bind(
      this, FunctionNameEnum.createOrder)) as Method<
        Request.CreateOrderRequest, Response.CreateOrderResponse>,
    attachPayment: (this.client.invoke.bind(
      this, FunctionNameEnum.attachPayment)) as Method<
        Request.AttachPaymentRequest, Response.AttachPaymentResponse>,
    void: (this.client.invoke.bind(
      this, FunctionNameEnum.voidOrder)) as Method<
        Request.VoidOrderRequest, Response.VoidOrderResponse>,
    update: (this.client.invoke.bind(
      this, FunctionNameEnum.updateOrder)) as Method<
        Request.UpdateOrderRequest, Response.UpdateOrderResponse>,
    list: (this.client.invoke.bind(
      this, FunctionNameEnum.getOrders)) as Method<
        Request.GetOrdersRequest, Response.GetOrdersResponse>,
    /**
     * Deprecated: Using `list` method
     */
    getRecentOrders: (this.client.invoke.bind(
      this, FunctionNameEnum.getRecentOrders)) as Method<
        Request.GetRecentOrdersRequest, Response.GetRecentOrdersResponse>,
    /**
     * Deprecated: Using `list` method
     */
    getOrdersByDatetimeRange: (this.client.invoke.bind(
      this, FunctionNameEnum.getOrdersByDatetimeRange)) as Method<
        Request.GetOrdersByDatetimeRangeRequest, Response.GetOrdersByDatetimeRangeResponse>,
    /**
     * Deprecated: Using `list` method
     */
    getOrdersByInvoiceNumber: (this.client.invoke.bind(
      this, FunctionNameEnum.getOrdersByInvoiceNumber)) as Method<
        Request.GetOrdersByInvoiceNumberRequest, Response.GetOrdersByInvoiceNumberResponse>,
    /**
     * Deprecated: Using `list` method
     */
    getOrdersByReceiptNumber: (this.client.invoke.bind(
      this, FunctionNameEnum.getOrdersByReceiptNumber)) as Method<
        Request.GetOrdersByReceiptNumberRequest, Response.GetOrdersByReceiptNumberResponse>,
  };

  public readonly shift = {
    closeShift: (this.client.invoke.bind(
      this, FunctionNameEnum.closeShift)) as Method<
        Request.CloseShiftRequest, Response.CloseShiftResponse>,
    getCurrentShiftClosing: (this.client.invoke.bind(
      this, FunctionNameEnum.getCurrentShiftClosing)) as Method<
        Request.GetCurrentShiftClosingRequest, Response.GetCurrentShiftClosingResponse>,
    getRecentShiftClosings: (this.client.invoke.bind(
      this, FunctionNameEnum.getRecentShiftClosings)) as Method<
        Request.GetRecentShiftClosingsRequest, Response.GetRecentShiftClosingsResponse>,
  };

  public readonly TSCBIPayment = {
    pay: (this.client.invoke.bind(
      this, FunctionNameEnum.TSCBIgwAPIPay)) as Method<
        Request.DomesticPayRequest, Response.DomesticPayResponse>,
    refund: (this.client.invoke.bind(
      this, FunctionNameEnum.TSCBIgwAPIRefund)) as Method<
        Request.DomesticRefundRequest, Response.DomesticRefundResponse>,
    query: (this.client.invoke.bind(
      this, FunctionNameEnum.TSCBIgwAPIQuery)) as Method<
        Request.DomesticQueryRequest, Response.DomesticQueryResponse>,
  };

  public readonly TSCBOPayment = {
    pay: (this.client.invoke.bind(
      this, FunctionNameEnum.TSCBOgwAPIPay)) as Method<
        Request.OverseasPayRequest, Response.OverseasPayResponse>,
    refund: (this.client.invoke.bind(
      this, FunctionNameEnum.TSCBOgwAPIRefund)) as Method<
        Request.OverseasRefundRequest, Response.OverseasRefundResponse>,
    query: (this.client.invoke.bind(
      this, FunctionNameEnum.TSCBOgwAPIQuery)) as Method<
        Request.OverseasQueryRequest, Response.OverseasQueryResponse>,
  };

  public readonly analysis = {
    getDailySummary: (this.client.invoke.bind(
      this, FunctionNameEnum.getDailySummary)) as Method<
        Request.GetDailySummaryRequest, Response.GetDailySummaryResponse>,
    getAnalysisData: (this.client.invoke.bind(
      this, FunctionNameEnum.getAnalysisData)) as Method<
        Request.GetAnalysisRequest, Response.GetAnalysisResponse>,
  };



}
